import React, {
  useCallback, useEffect, useRef, useState,
} from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import ModalContainer from '../../../../../Modals/ModalContainer'
import { hideModal, showErrorNotification, showSuccesNotification } from '../../../../../../redux/actions'
import { useDispatch } from 'react-redux'
import { Alert, Box, Link, Typography } from '@mui/material'
import { FormAutoCompleteInput } from '../../../../../@Common/Form/AutoComplete/FormAutoComplete'
import { MUIFormCheckboxInput } from '../../../../../@Common/Form/CheckboxInput/FormCheckboxInput'
import useCatalog from './useCatalog'
import useIntegrations from './useIntegrations'
import { getStoreDisplayName } from './storeUtils'
import { useLazyCheckManufacturerAvailabilityQuery } from '../../../../../../redux/slices/integrationsSlice'

const ManufacturerModal = ({
  partnerListingId,
  selectedApp,
  isRevokeMode,
}) => {
  const dispatch = useDispatch()
  const integrationType = selectedApp.integrationType
  const integratedApplicationName = selectedApp.name

  const { control, watch, getValues } = useForm({
    defaultValues: {
      manufacturer: null,
      productType: 'new',
    },
  })

  const selectedManufacturer = watch('manufacturer')

  const { searchManufacturersByTerm, searchManufacturersResults } = useCatalog()
  const { createPartnerListingGovxShopIntegration, deletePartnerListingIntegration } = useIntegrations(partnerListingId)

  const [checkAvailability] = useLazyCheckManufacturerAvailabilityQuery()
  const [availabilityWarning, setAvailabilityWarning] = useState(null)
  const [availabilityMessage, setAvailabilityMessage] = useState(null)
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const availabilityRequestId = useRef(0)

  const [inputValue, setInputValue] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const timeoutRef = useRef(null)

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  const handleManufacturerInputChange = useCallback((_event, value, reason) => {
    if (reason !== 'input') {
      return
    }

    setInputValue(value)
    clearTimeout(timeoutRef.current)
    if (!value) {
      setHasSearched(false)
      return
    }
    timeoutRef.current = setTimeout(() => {
      setHasSearched(true)
      searchManufacturersByTerm(value)
    }, 250)
  }, [searchManufacturersByTerm])

  const getOptionLabel = (option) => {
    if (!option) return ''
    return `${option.manufacturerName} (${option.manufacturerId})`
  }

  const getSelectedStoreIds = () => {
    const values = getValues()
    return selectedManufacturer?.stores
      ?.filter((store) => values[`store_${store.storeId}`])
      .map((store) => store.storeId) || []
  }

  const checkManufacturerAvailability = useCallback(async (manufacturer) => {
    availabilityRequestId.current += 1
    const requestId = availabilityRequestId.current
    setAvailabilityWarning(null)
    setAvailabilityMessage(null)

    if (!manufacturer) {
      return null
    }

    setIsCheckingAvailability(true)
    setAvailabilityMessage('Checking whether this manufacturer is already linked...')
    let result = { conflictingSellerId: null }
    try {
      result = await checkAvailability({
        manufacturerId: String(manufacturer.manufacturerId),
        partnerListingId,
      }).unwrap()
    } catch (err) {
      console.error(err)
    } finally {
      if (requestId === availabilityRequestId.current) {
        setIsCheckingAvailability(false)
        setAvailabilityMessage(null)
      }
    }

    if (requestId !== availabilityRequestId.current) {
      return null
    }

    if (result.conflictingSellerId) {
      const warning = {
        listingId: result.conflictingSellerListingId,
        sellerName: result.conflictingSellerName || result.conflictingSellerId,
      }
      setAvailabilityMessage(null)
      setAvailabilityWarning(warning)
      return warning
    }

    setAvailabilityMessage(null)
    return null
  }, [checkAvailability, partnerListingId])

  const handleManufacturerChange = useCallback((_event, manufacturer) => {
    checkManufacturerAvailability(manufacturer)
  }, [checkManufacturerAvailability])

  const handleSave = async () => {
    if (!selectedManufacturer) return
    const manufacturer = selectedManufacturer

    if (availabilityWarning || await checkManufacturerAvailability(manufacturer)) {
      return
    }

    const storeIds = getSelectedStoreIds()
    await createPartnerListingGovxShopIntegration(
      String(manufacturer.manufacturerId),
      new Date().toISOString(),
      storeIds,
    )
    dispatch(hideModal())
  }

  const handleRevokeAccess = async () => {
    try {
      await deletePartnerListingIntegration(
        integrationType,
        integratedApplicationName
      )
      dispatch(hideModal())
      dispatch(showSuccesNotification(`"${integratedApplicationName}" access successfully revoked from this listing.`))
    } catch (err) {
      console.error(err)
      dispatch(showErrorNotification(`Failed to remove "${integratedApplicationName}" from this listing.`))
    }
  }

  return (
    <ModalContainer
      title={isRevokeMode ? `Revoke ${integratedApplicationName} Access` : `Connect Manufacturer`}
      maxWidth="sm"
      actions={{
        primaryAction: {
          label: isRevokeMode ? 'Delete' : 'Connect',
          onClick: () => {
            if (isRevokeMode) {
              handleRevokeAccess()
            } else {
              handleSave()
            }
          },
          disabled: !isRevokeMode && (!selectedManufacturer || !!availabilityWarning || isCheckingAvailability),
          color: isRevokeMode ? 'error' : 'primary',
        },
        secondaryAction: {
          label: 'Cancel',
          onClick: () => dispatch(hideModal()),
        },
      }}
    >
      {isRevokeMode ? (
        <Typography>
          Are you sure you want to delete the link to {integratedApplicationName}? Deleting the link will impact reporting and how the seller appears on the site.
        </Typography>
      ) : (
        <>
          <FormAutoCompleteInput
            name="manufacturer"
            control={control}
            label="Manufacturer"
            options={
              hasSearched && inputValue && searchManufacturersResults?.data?.length
                ? searchManufacturersResults.data
                : []
            }
            noOptionsText={
              !inputValue || !hasSearched
                ? 'Search by name or ID'
                : searchManufacturersResults?.isLoading
                  ? 'Loading...'
                  : 'No manufacturer found'
            }
            itemLabelKey="manufacturerName"
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option.manufacturerId === value.manufacturerId}
            onChange={handleManufacturerChange}
            onInputChange={handleManufacturerInputChange}
            freeSolo={false}
            rules={{ required: true }}
          />
          <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block' }}>
            Search by manufacturer name or ID
          </Typography>

          {availabilityMessage && !availabilityWarning && (
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                p: 1.5,
                color: '#666',
                backgroundColor: '#F5F5F5',
                border: '1px solid #E0E0E0',
                borderRadius: '4px',
              }}
            >
              {availabilityMessage}
            </Typography>
          )}

          {availabilityWarning && (
            <Alert
              severity="warning"
              sx={{
                mt: 1.5,
                color: '#664D03',
                backgroundColor: '#FFF3CD',
                border: '1px solid #FFECB5',
                '& .MuiAlert-icon': {
                  color: '#856404',
                },
              }}
            >
              This manufacturer is already linked to{' '}
              {availabilityWarning.listingId ? (
                <Link
                  href={`/partners/listings/${availabilityWarning.listingId}/settings/integrations`}
                  sx={{ color: '#664D03', fontWeight: 700, textDecorationColor: '#664D03' }}
                >
                  {availabilityWarning.sellerName}
                </Link>
              ) : availabilityWarning.sellerName}
              .
            </Alert>
          )}

          {selectedManufacturer && (
            <>
              {/* Stores */}
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 2.5, mt: 2 }}>
                {selectedManufacturer.stores?.length > 0 ? (
                  <Box display="flex" alignItems="flex-start">
                    <Box sx={{ minWidth: '250px', marginRight: '40px' }}>
                      <Typography sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 0.5 }}>Stores</Typography>
                      <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.5 }}>
                        Select the stores from which you want to pull products for this seller.
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: '250px', marginRight: '40px' }} display="flex" flexDirection="column">
                      {selectedManufacturer.stores.map((store) => (
                        <MUIFormCheckboxInput
                          key={store.storeId}
                          name={`store_${store.storeId}`}
                          label={getStoreDisplayName(store.storeName)}
                          control={control}
                          defaultValue={true}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    No stores related to this manufacturer through any active product variants.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </>
      )}
    </ModalContainer>
  )
}

ManufacturerModal.propTypes = {
  partnerListingId: PropTypes.string.isRequired,
  selectedApp: PropTypes.shape({
    integratedApplicationName: PropTypes.string.isRequired,
    integrationType: PropTypes.string.isRequired,
  }).isRequired,
  isRevokeMode: PropTypes.bool.isRequired,
}

export default ManufacturerModal
